<!--
VueSelectList Component - Generic Select Dropdown Component (PrimeVue AutoComplete)

USE CASES:
==========

1. FRANCHISE SELECTION:
   - Use for selecting franchises in forms and filters
   - Data source: eventBus.emit('filter.listFranchisesArraySelect')
   - Returns JSON array format:
     [
       {
         "Name": "Pato Branco",
         "ID": "14"
       },
       {
         "Name": "Francisco Beltrão", 
         "ID": "50"
       }
     ]

2. LOCATION SELECTION:
   - Use for selecting locations/localidades
   - Data source: Location service API endpoints
   - Same JSON array format as franchises

3. USER SELECTION:
   - Use for selecting users, technicians, or staff members
   - Data source: User management APIs
   - Same JSON array format

4. CATEGORY SELECTION:
   - Use for selecting service categories, expense categories, etc.
   - Data source: Category management APIs
   - Same JSON array format

5. STATUS SELECTION:
   - Use for selecting status values (active/inactive, pending/completed, etc.)
   - Data source: Static arrays or configuration APIs
   - Same JSON array format

FEATURES:
=========
- Auto-selects first available option when no value is set
- Uses PrimeVue AutoComplete with dropdown for enhanced UI
- Supports validation with visual indicators
- Handles disabled state
- Supports required field validation
- Emits both v-model and legacy input events
- Responsive layout with optional labels
- Searchable dropdown with typeahead

PROPS:
======
- itensSelect: Array of objects with {ID, Name} structure (optional
  `Description` renders as a subtitle under the option)
- elemId: Unique identifier for the select element
- placeholder: Placeholder text for the dropdown
- label: Optional label text (if empty, uses full width)
- validation: Function that returns boolean for validation state
- ngRequired: Boolean/string indicating if field is required
- disabled: Boolean/string indicating if field is disabled
- modelValue: Current selected value (v-model)
- value: Legacy value prop for backward compatibility
- callback: Optional function to call when value changes
-->

<template>
  <div class="select-list-wrapper">
    <!-- Label with validation indicators -->
    <label
      v-if="label && label !== ''"
      :for="elemId"
      :data-testid="elemId ? `${elemId}-label` : undefined"
      class="select-list-label"
      :class="{
        'text-warning': !hasValidation && isRequired,
        'text-success': hasValidation && isRequired
      }"
    >
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-indicator">*</span>
      {{ label }}
    </label>

    <!-- PrimeVue AutoComplete with dropdown -->
    <AutoComplete
      :inputId="elemId"
      v-model="selectedItem"
      :suggestions="filteredSuggestions"
      @complete="onComplete"
      @change="onChange"
      optionLabel="Name"
      dropdown
      :disabled="isDisabled"
      :placeholder="placeholder || ''"
      :invalid="!hasValidation && isRequired"
      forceSelection
      fluid
      appendTo="body"
      :pt="ptOverrides"
    >
      <template #option="{ option }">
        <div class="select-list-option">
          <span>{{ option.Name }}</span>
          <small
            v-if="option.Description"
            class="select-list-option-description"
            :data-testid="elemId ? `${elemId}-option-${option.ID}-description` : undefined"
          >{{ option.Description }}</small>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import AutoComplete from 'primevue/autocomplete';
import { log } from '../utils/DebugLogger';

const { t } = useI18n();

interface SelectItem {
  ID: string | number;
  Name: string;
  [key: string]: any;
}

const props = defineProps({
  // Optional callback function triggered when selection changes
  callback: Function as import('vue').PropType<(value: string | number) => void>,

  // Array of objects with {ID, Name} structure for dropdown options
  itensSelect: {
    type: Array as import('vue').PropType<SelectItem[]>,
    default: () => []
  },

  // Unique identifier for the select element
  elemId: String,

  // Placeholder text shown when no option is selected
  placeholder: String,

  // Optional label text displayed to the left of the select
  label: String,

  // Validation function that returns boolean (true = valid, false = invalid)
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },

  // Indicates if the field is required (shows warning icon when invalid)
  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  // Indicates if the field should be disabled
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // Current selected value (v-model support)
  modelValue: [String, Number],

  // Legacy value prop for backward compatibility
  value: [String, Number],

  // Whether to auto-select the first available option
  autoSelectFirst: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  'input': [value: string | number];
  'selected': [item: SelectItem | null];
}>();

// Reactive data
const selectedItem = ref<SelectItem | null>(null);
const filteredSuggestions = ref<SelectItem[]>([]);

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  props.validation()
);

// Conditionally inject data-testid attrs only when elemId is provided —
// otherwise PrimeVue would render `undefined-dropdown-btn`, `undefined-input`, etc.
const ptOverrides = computed(() => {
  const id = props.elemId;
  // PrimeVue's AutoComplete dropdown toggle is ICON-ONLY, so without an explicit
  // aria-label it has no accessible name (axe: button-name, impact critical).
  // SelectList is used by every form in the app, so the name is set here, always
  // — not only when `elemId` happens to be provided.
  const dropdownAttrs: Record<string, string> = {
    'aria-label': props.label ? `${t('Abrir lista')} - ${props.label}` : t('Abrir lista'),
  };
  const overrides: Record<string, unknown> = {
    option: (ctx: { context?: { option?: { ID?: string | number } } }) => {
      const attrs: Record<string, string> = {};
      const optionId = ctx?.context?.option?.ID;
      if (id) attrs['data-testid'] = optionId != null ? `${id}-option-${optionId}` : `${id}-option`;
      if (optionId != null) attrs['data-option-id'] = String(optionId);
      return attrs;
    },
    dropdown: dropdownAttrs,
  };
  if (id) {
    overrides.input = { 'data-testid': `${id}-input` };
    dropdownAttrs['data-testid'] = `${id}-dropdown-btn`;
    overrides.overlay = { 'data-testid': `${id}-panel` };
  }
  return overrides;
});

// Methods
const findItemById = (id: string | number): SelectItem | null => {
  if (!id) return null;
  return props.itensSelect.find(item => item.ID == id) ?? null;
};

const onComplete = (event: { query: string }): void => {
  const query = event.query.toLowerCase();
  if (!query) {
    filteredSuggestions.value = [...props.itensSelect];
  } else {
    filteredSuggestions.value = props.itensSelect.filter(item => {
      const name = (item && item.Name != null ? String(item.Name) : '').toLowerCase();
      const id = (item && item.ID != null ? String(item.ID) : '').toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }
};

const onChange = (): void => {
  // Extract ID from selected item object
  const newValue = selectedItem.value ? selectedItem.value.ID : '';
  log('SelectList', 'onChange', {
    selectedItem: selectedItem.value,
    extractedValue: newValue
  });

  emit('update:modelValue', newValue);
  emit('input', newValue); // Keep for backward compatibility
  emit('selected', selectedItem.value);

  if (props.callback) {
    props.callback(newValue);
  }
};

const autoSelectFirstValue = (): void => {
  // Only auto-select if prop is true, no value is currently selected and there are items available
  if (!props.autoSelectFirst) {
    log('SelectList', 'autoSelectFirstValue', 'Auto-select DISABLED by prop');
    return;
  }

  if (!selectedItem.value && props.itensSelect && props.itensSelect.length > 0) {
    // Skip the placeholder option (empty value) and select the first actual item
    const firstItem = props.itensSelect.find(item => item.ID && item.ID !== '');
    if (firstItem) {
      log('SelectList', 'autoSelectFirstValue', 'Auto-selecting first value:', firstItem);
      selectedItem.value = firstItem;
      onChange();
    }
  }
};

const syncSelectedItem = (value: string | number | undefined | null): void => {
  log('SelectList', 'syncSelectedItem', 'SyncSelectedItem called with:', value);

  if (!value || value === '') {
    selectedItem.value = null;
  } else {
    const item = findItemById(value);
    log('SelectList', 'syncSelectedItem', 'Found item for value:', item);
    selectedItem.value = item;
  }
};

// Watchers
watch(() => props.modelValue, (newVal) => {
  log('SelectList', 'watch:modelValue', 'modelValue changed:', newVal);
  syncSelectedItem(newVal);
});

watch(() => props.value, (newVal) => {
  log('SelectList', 'watch:value', 'value changed:', newVal);
  syncSelectedItem(newVal);
});

watch(() => props.itensSelect, (newItems) => {
  log('SelectList', 'watch:itensSelect', 'itensSelect changed:', newItems);
  filteredSuggestions.value = [...(newItems || [])];
  nextTick(() => {
    const currentValue = props.modelValue || props.value;
    if (currentValue) {
      syncSelectedItem(currentValue);
    } else {
      autoSelectFirstValue();
    }
  });
}, { deep: true, immediate: true });

// Initialize on mount
nextTick(() => {
  log('SelectList', 'mounted', 'Component mounted', {
    modelValue: props.modelValue,
    value: props.value,
    itensSelect: props.itensSelect
  });
  
  const initialValue = props.modelValue || props.value;
  if (initialValue) {
    syncSelectedItem(initialValue);
  } else {
    autoSelectFirstValue();
  }
});
</script>

<style scoped>
.select-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2em;
}

.select-list-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.select-list-label.text-warning {
  color: var(--yellow-500);
}

.select-list-label.text-success {
  color: var(--green-500);
}

.select-list-option {
  display: flex;
  flex-direction: column;
}

.select-list-option-description {
  color: var(--p-text-muted-color);
  white-space: normal;
}

.required-indicator {
  color: var(--red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--yellow-500);
}

.text-success {
  color: var(--green-500);
}
</style>
